import { useEffect, useMemo, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import { documentEvidenceHttp } from '@/services/documents'
import { returnCasesHttp } from '@/services/returnCases'
import { toast } from '@/store/toastStore'
import type { ExtractionCandidateReviewItem } from '@/types/returnCase.types'

interface Props {
  caseId: string
  enabled: boolean
  onChanged?: () => Promise<void> | void
}

const pretty = (value: unknown) =>
  typeof value === 'string' ? value : JSON.stringify(value, null, 2)

const parseCorrection = (value: string): unknown => {
  try {
    return JSON.parse(value)
  } catch {
    return value
  }
}

export default function ExtractionCandidateReviewPanel({ caseId, enabled, onChanged }: Props) {
  const queryClient = useQueryClient()
  const queryKey = ['extraction-candidates', caseId]
  const candidates = useQuery({
    queryKey,
    queryFn: () => returnCasesHttp.extractionCandidates(caseId),
    enabled,
  })
  const pending = useMemo(
    () => candidates.data?.items.filter(item => !item.disposition) ?? [],
    [candidates.data]
  )
  const [selectedId, setSelectedId] = useState('')
  const selected =
    candidates.data?.items.find(item => item.candidate_id === selectedId) ?? pending[0]
  const [reason, setReason] = useState('')
  const [correctedValue, setCorrectedValue] = useState('')
  const [correctionReason, setCorrectionReason] = useState('')
  const [previewUrl, setPreviewUrl] = useState('')
  const [previewError, setPreviewError] = useState('')

  useEffect(() => {
    if (selected && !selectedId) setSelectedId(selected.candidate_id)
    if (selected) setCorrectedValue(pretty(selected.normalized_value))
  }, [selected?.candidate_id, selectedId])

  useEffect(
    () => () => {
      if (previewUrl) URL.revokeObjectURL(previewUrl)
    },
    [previewUrl]
  )

  const refresh = async () => {
    await queryClient.invalidateQueries({ queryKey })
    await onChanged?.()
  }
  const decide = useMutation({
    mutationFn: async (action: 'accept' | 'correct' | 'reject') => {
      if (!selected || !candidates.data) throw new Error('Choose a candidate to review')
      if (reason.trim().length < 3) throw new Error('Enter a review reason')
      const common = {
        expected_revision: candidates.data.case_revision,
        displayed_candidate_sha256: selected.candidate_sha256,
        reason: reason.trim(),
      }
      if (action === 'accept')
        return returnCasesHttp.acceptExtractionCandidate(caseId, selected.candidate_id, common)
      if (action === 'reject')
        return returnCasesHttp.rejectExtractionCandidate(caseId, selected.candidate_id, common)
      if (correctionReason.trim().length < 3)
        throw new Error('Explain why the proposed value was corrected')
      return returnCasesHttp.correctExtractionCandidate(caseId, selected.candidate_id, {
        ...common,
        corrected_value: parseCorrection(correctedValue),
        correction_reason: correctionReason.trim(),
      })
    },
    onSuccess: async result => {
      setReason('')
      setCorrectionReason('')
      setSelectedId('')
      await refresh()
      toast.success(`Candidate ${result.disposition.action.toLowerCase()}`)
    },
    onError: async error => {
      await refresh()
      toast.error(
        'Candidate decision rejected',
        error instanceof Error ? error.message : 'Reload and review the current candidate.'
      )
    },
  })

  const openSource = async (item: ExtractionCandidateReviewItem) => {
    const location = item.locations[0]
    try {
      const grant = await documentEvidenceHttp.previewGrant(
        caseId,
        item.document_id,
        item.document_version_id,
        location.page_number
      )
      if (!grant.preview_available) {
        setPreviewError('Page preview is unavailable. Use the accessible original document.')
        return
      }
      const blob = await documentEvidenceHttp.redeem(grant.access_url)
      if (previewUrl) URL.revokeObjectURL(previewUrl)
      setPreviewUrl(URL.createObjectURL(blob))
      setPreviewError('')
    } catch (error) {
      setPreviewError(error instanceof Error ? error.message : 'Source preview could not be opened')
    }
  }

  if (!enabled) return null
  return (
    <section className="card mt-6 p-5" aria-labelledby="candidate-review-heading">
      <h2 id="candidate-review-heading" className="font-semibold">
        Extraction candidate review
      </h2>
      <p className="mt-1 text-sm text-text-secondary">
        Extraction never changes the return automatically. Review each source and make one explicit
        decision.
      </p>
      {candidates.isLoading && <p className="mt-4">Loading reviewable candidates…</p>}
      {candidates.isError && (
        <div className="alert alert-error mt-4" role="alert">
          Candidates could not be loaded. Manual data entry remains available.
        </div>
      )}
      {candidates.data && candidates.data.items.length === 0 && (
        <p className="mt-4 text-sm text-text-secondary">No extraction candidates are available.</p>
      )}
      {selected && (
        <div className="mt-4 grid gap-4 lg:grid-cols-2">
          <div className="rounded border border-border p-4">
            <label className="block text-sm" htmlFor="candidate-choice">
              Candidate
            </label>
            <select
              id="candidate-choice"
              className="input mt-1 w-full"
              value={selected.candidate_id}
              onChange={event => setSelectedId(event.target.value)}
            >
              {candidates.data?.items.map(item => (
                <option key={item.candidate_id} value={item.candidate_id}>
                  {item.fact_key} · {item.confidence_band} · {item.disposition?.action ?? 'PENDING'}
                </option>
              ))}
            </select>
            <div className="mt-4 flex flex-wrap gap-2 text-xs">
              <span className="badge">{selected.confidence_band}</span>
              <span>Score {selected.confidence_score}</span>
              {selected.high_impact && <span className="badge badge-warning">High impact</span>}
            </div>
            <dl className="mt-3 space-y-2 text-sm">
              <div>
                <dt className="text-text-secondary">Original</dt>
                <dd className="break-all font-mono">{pretty(selected.raw_value)}</dd>
              </div>
              <div>
                <dt className="text-text-secondary">Proposed</dt>
                <dd className="break-all font-mono">{pretty(selected.normalized_value)}</dd>
              </div>
              <div>
                <dt className="text-text-secondary">Calibration</dt>
                <dd>{selected.calibration_version}</dd>
              </div>
              <div>
                <dt className="text-text-secondary">Extractor lineage</dt>
                <dd>
                  {selected.lineage.extractor_key} {selected.lineage.extractor_version} ·{' '}
                  {selected.lineage.provider} · parser {selected.lineage.parser_version} · model{' '}
                  {selected.lineage.model_version}
                </dd>
              </div>
              <div>
                <dt className="text-text-secondary">Source</dt>
                <dd>
                  Document v{selected.document_version_number}, page{' '}
                  {selected.locations[0].page_number}
                </dd>
              </div>
              <div>
                <dt className="text-text-secondary">Candidate checksum</dt>
                <dd className="break-all font-mono text-xs">{selected.candidate_sha256}</dd>
              </div>
            </dl>
            <button
              className="btn btn-secondary mt-4"
              type="button"
              onClick={() => openSource(selected)}
            >
              View exact source region
            </button>
            {previewError && (
              <div className="alert alert-warning mt-3" role="status">
                {previewError}
              </div>
            )}
          </div>
          <div className="rounded border border-border p-4">
            {previewUrl ? (
              <figure>
                <div className="relative overflow-hidden rounded border border-border">
                  <img
                    src={previewUrl}
                    alt={`Source page ${selected.locations[0].page_number} for ${selected.fact_key}`}
                    className="block w-full"
                  />
                  {selected.locations[0].rectangle && (
                    <span
                      aria-hidden="true"
                      className="absolute border-2 border-red-600 bg-red-200/20"
                      style={{
                        left: `${selected.locations[0].rectangle.x * 100}%`,
                        top: `${selected.locations[0].rectangle.y * 100}%`,
                        width: `${selected.locations[0].rectangle.width * 100}%`,
                        height: `${selected.locations[0].rectangle.height * 100}%`,
                      }}
                    />
                  )}
                </div>
                <figcaption className="mt-2 text-xs">
                  Normalized source coordinates; no value is accepted by opening this preview.
                </figcaption>
              </figure>
            ) : (
              <p className="text-sm text-text-secondary">
                Open the exact source to compare the document beside the proposed value.
              </p>
            )}
            {selected.disposition ? (
              <div className="mt-4 rounded border border-border p-3 text-sm" role="status">
                <strong>{selected.disposition.action}</strong> at revision{' '}
                {selected.disposition.case_revision}
                <div className="mt-1">{selected.disposition.reason}</div>
              </div>
            ) : (
              <div className="mt-4 space-y-3">
                <label className="block text-sm" htmlFor="candidate-reason">
                  Review reason
                </label>
                <textarea
                  id="candidate-reason"
                  className="input w-full"
                  value={reason}
                  onChange={event => setReason(event.target.value)}
                />
                <details>
                  <summary className="cursor-pointer text-sm font-medium">
                    Correct proposed value
                  </summary>
                  <label className="mt-3 block text-sm" htmlFor="candidate-corrected-value">
                    Corrected value (JSON or text)
                  </label>
                  <textarea
                    id="candidate-corrected-value"
                    className="input mt-1 min-h-24 w-full font-mono"
                    value={correctedValue}
                    onChange={event => setCorrectedValue(event.target.value)}
                  />
                  <label className="mt-3 block text-sm" htmlFor="candidate-correction-reason">
                    Correction explanation
                  </label>
                  <textarea
                    id="candidate-correction-reason"
                    className="input mt-1 w-full"
                    value={correctionReason}
                    onChange={event => setCorrectionReason(event.target.value)}
                  />
                  <button
                    className="btn btn-primary mt-3"
                    type="button"
                    disabled={decide.isPending}
                    onClick={() => decide.mutate('correct')}
                  >
                    Correct and accept this candidate
                  </button>
                </details>
                <div className="flex flex-wrap gap-2">
                  <button
                    className="btn btn-primary"
                    type="button"
                    disabled={decide.isPending}
                    onClick={() => decide.mutate('accept')}
                  >
                    Accept this candidate
                  </button>
                  <button
                    className="btn btn-secondary"
                    type="button"
                    disabled={decide.isPending}
                    onClick={() => decide.mutate('reject')}
                  >
                    Reject this candidate
                  </button>
                </div>
                <p className="text-xs text-text-secondary">
                  Bulk acceptance is intentionally unavailable. Every candidate requires an
                  individual decision.
                </p>
              </div>
            )}
          </div>
        </div>
      )}
    </section>
  )
}
